import { TnplAcceptableRiskLevel } from "@redotech/redo-tnpl-settings-common/tnpl-settings";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum TnplContractStatus {
  TRIAL_ACTIVE = "trial_active",
  CANCELLED = "cancelled",
  COMPLETE_PAID_BY_CUSTOMER = "complete_paid_by_customer",
  COMPLETE_RETURNED = "complete_returned",
}

export enum ShippingDueTrigger {
  ORDER_PURCHASE = "order_purchase",
  TRIAL_END = "trial_end",
}

export enum TnplChargeType {
  AUTHORIZATION = "authorization",
  CAPTURE_PREVIOUS_AUTHORIZATION = "capture_previous_authorization",
  AUTHORIZE_AND_CAPTURE = "authorize_and_capture",
}

export enum TnplChargeStatus {
  PENDING = "pending",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  API_FAILURE = "api_failure",
}

export enum TnplCancellationType {
  INITIAL_PAYMENT_FAILED = "initial_payment_failed",
  INELIGIBLE_FOR_TRIAL = "ineligible_for_trial",
}

export enum TnplOrderIneligibleReasonType {
  FRAUD_RISK = "fraud_risk",
  CART_VALUE_EXCEEDED = "cart_value_exceeded",
  CART_ITEM_COUNT_EXCEEDED = "cart_item_count_exceeded",
}

// Zod schemas for each ineligible reason type
const FraudRiskReasonSchema = z.object({
  type: z
    .literal(TnplOrderIneligibleReasonType.FRAUD_RISK)
    .meta({ privacy: privacy.nonPii }),
  highestRiskLevel: createEnum(TnplAcceptableRiskLevel, {
    enumName: "TnplAcceptableRiskLevel",
  }).meta({ privacy: privacy.nonPii }),
  acceptableRiskLevel: createEnum(TnplAcceptableRiskLevel, {
    enumName: "TnplAcceptableRiskLevel",
  }).meta({ privacy: privacy.nonPii }),
});

const CartValueExceededReasonSchema = z.object({
  type: z
    .literal(TnplOrderIneligibleReasonType.CART_VALUE_EXCEEDED)
    .meta({ privacy: privacy.nonPii }),
  cartValue: z.number().meta({ privacy: privacy.nonPii }),
  cartValueLimit: z.number().meta({ privacy: privacy.nonPii }),
});

const CartItemCountExceededReasonSchema = z.object({
  type: z
    .literal(TnplOrderIneligibleReasonType.CART_ITEM_COUNT_EXCEEDED)
    .meta({ privacy: privacy.nonPii }),
  cartTnplItemCount: z.number().meta({ privacy: privacy.nonPii }),
  cartMaxTnplItems: z.number().meta({ privacy: privacy.nonPii }),
});

// Union schema for all ineligible reason types
export const TnplOrderIneligibleReasonSchema = z.discriminatedUnion("type", [
  FraudRiskReasonSchema.meta({ privacy: privacy.structured }),
  CartValueExceededReasonSchema.meta({ privacy: privacy.structured }),
  CartItemCountExceededReasonSchema.meta({ privacy: privacy.structured }),
]);

// TypeScript type derived from the Zod schema
export type TnplOrderIneligibleReason = z.infer<
  typeof TnplOrderIneligibleReasonSchema
>;

export const TnplChargeSchema = z.object({
  idempotencyKey: z.string().meta({ privacy: privacy.nonPii }),
  paymentReferenceId: z.string().meta({ privacy: privacy.nonPii }),
  transactionId: z.string().optional().meta({ privacy: privacy.nonPii }),
  parentTransactionId: z.string().optional().meta({ privacy: privacy.nonPii }),
  date: z.date().meta({ privacy: privacy.nonPii }),
  type: createEnum(TnplChargeType, { enumName: "TnplChargeType" }).meta({
    privacy: privacy.nonPii,
  }),
  status: createEnum(TnplChargeStatus, { enumName: "TnplChargeStatus" }).meta({
    privacy: privacy.nonPii,
  }),
  amount: z.number().optional().meta({ privacy: privacy.nonPii }),
  jobId: z.string().optional().meta({ privacy: privacy.nonPii }),
});

export type TnplCharge = z.infer<typeof TnplChargeSchema>;

export const TnplContractSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  team: zExt.objectId().meta({ privacy: privacy.nonPii }),
  tnplContractTemplate: zExt.objectId().meta({ privacy: privacy.nonPii }),
  shopifyOrderId: z.number().meta({ privacy: privacy.nonPii }),
  shopifyLineItemIds: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .meta({ privacy: privacy.nonPii }),
  shopifySellingPlanId: z.number().meta({ privacy: privacy.nonPii }),
  shopifyPaymentScheduleIds: z
    .array(z.number().meta({ privacy: privacy.nonPii }))
    .optional()
    .meta({ privacy: privacy.nonPii }),
  shopifyPaymentMandateIds: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .meta({ privacy: privacy.nonPii }),
  initialSellingPlanAmount: z.number().meta({ privacy: privacy.nonPii }),
  remainingTrialAmount: z.number().optional().meta({ privacy: privacy.nonPii }),
  sellingPlanCurrency: z.string().meta({ privacy: privacy.nonPii }),
  order: zExt.objectId().meta({ privacy: privacy.nonPii }),
  return: zExt.objectId().optional().meta({ privacy: privacy.nonPii }),
  orderDate: z.date().meta({ privacy: privacy.nonPii }),
  initialPaymentDate: z.date().optional().meta({ privacy: privacy.nonPii }),
  deliveryDate: z.date().optional().meta({ privacy: privacy.nonPii }),
  returnedDate: z.date().optional().meta({ privacy: privacy.nonPii }),
  approvedDate: z.date().optional().meta({ privacy: privacy.nonPii }),
  trialPeriodEnd: z.date().optional().meta({ privacy: privacy.nonPii }),
  chargeDate: z.date().optional().meta({ privacy: privacy.nonPii }),
  merchantProcessingEndDate: z
    .date()
    .optional()
    .meta({ privacy: privacy.nonPii }),
  trialDatesCommitted: z
    .boolean()
    .optional()
    .default(false)
    .meta({ privacy: privacy.nonPii }),
  status: createEnum(TnplContractStatus, {
    enumName: "TnplContractStatus",
  }).meta({ privacy: privacy.nonPii }),
  charges: z
    .array(TnplChargeSchema.meta({ privacy: privacy.structured }))
    .meta({ privacy: privacy.structured }),
  orderRiskLevel: z.string().optional().meta({ privacy: privacy.nonPii }),
  orderIneligibleReason: TnplOrderIneligibleReasonSchema.optional().meta({
    privacy: privacy.structured,
  }),
  cancelledReason: createEnum(TnplCancellationType, {
    enumName: "TnplCancellationType",
  })
    .optional()
    .meta({ privacy: privacy.nonPii }),
  defaultedAt: z.date().optional().meta({ privacy: privacy.nonPii }),
  shippingDueLater: z.number().nullish().meta({ privacy: privacy.nonPii }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  updatedAt: z.date().meta({ privacy: privacy.nonPii }),
});

export type TnplContract = z.infer<typeof TnplContractSchema>;
export interface ITnplContract extends z.ZodType<TnplContract> {}

export const MERCHANT_PROCESSING_TIME_DAYS = 10; // Delay payment capture by 10 days when return is pending

/**
 * Status display mapping for merchant-friendly TNPL contract status labels
 */
const TNPL_STATUS_DISPLAY_MAP: Record<TnplContractStatus, string> = {
  [TnplContractStatus.TRIAL_ACTIVE]: "Active",
  [TnplContractStatus.CANCELLED]: "Cancelled",
  [TnplContractStatus.COMPLETE_PAID_BY_CUSTOMER]: "Paid",
  [TnplContractStatus.COMPLETE_RETURNED]: "Returned",
};

/**
 * Formats a TNPL contract status for merchant display
 * @param status - The raw TNPL contract status
 * @returns A formatted, merchant-friendly status string
 */
export function formatTnplContractStatus(status: string): string {
  const statusEnum = status as TnplContractStatus;
  return (
    TNPL_STATUS_DISPLAY_MAP[statusEnum] ||
    status
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l: string) => l.toUpperCase())
  );
}
